import { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { ChevronDown, Settings } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface AdvancedSettingsProps {
  backendUrl: string;
  cameraIp: string;
  onBackendUrlChange: (value: string) => void;
  onCameraIpChange: (value: string) => void;
  disabled?: boolean;
}

export function AdvancedSettings({
  backendUrl,
  cameraIp,
  onBackendUrlChange,
  onCameraIpChange,
  disabled = false,
}: AdvancedSettingsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger
        type="button"
        className="flex w-full items-center justify-between rounded-xl border bg-card/60 px-4 py-3 text-sm font-medium transition-colors outline-none hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
        disabled={disabled}
      >
        <span className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-primary" />
          {t('traceability.scanPage.keyence.advanced.title')}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3 space-y-4 rounded-xl border bg-card/60 p-4 shadow-inner">
        <div className="space-y-2">
          <Label htmlFor="keyence-backend" className="text-xs font-medium">
            {t('traceability.scanPage.keyence.backendLabel')}
          </Label>
          <Input
            id="keyence-backend"
            placeholder={t('traceability.scanPage.keyence.backendPlaceholder')}
            value={backendUrl}
            onChange={(event) => onBackendUrlChange(event.target.value)}
            disabled={disabled}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="keyence-ip" className="text-xs font-medium">
            {t('traceability.scanPage.keyence.ipLabel')}
          </Label>
          <Input
            id="keyence-ip"
            placeholder={t('traceability.scanPage.keyence.ipPlaceholder')}
            value={cameraIp}
            onChange={(event) => onCameraIpChange(event.target.value)}
            disabled={disabled}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
